import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Subscription, catchError, of } from 'rxjs';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { PersonSelectComponent, PersonOption } from '../../components/person-select/person-select.component';
import { DefectService } from '../../services/defect.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { TestRunService } from '../../services/test-run.service';
import { Defect, DefectEvidence, DefectStatus, UpdateDefectRequest } from '../../models/defect.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { saveBlob } from '../../shared/file-download';
import { serverMessage } from '../object-mapping/server-message';
import { statusChoices } from './defect-status';

type LoadState = 'loading' | 'ok' | 'notFound' | 'error';

const CONFLICT_KEYS: Record<string, string> = {
  DEFECT_NO_PASSED_RETEST: 'defect.detail.error.noPassedRetest',
  DEFECT_VERSION_CONFLICT: 'defect.detail.error.versionConflict',
  DEFECT_INVALID_TRANSITION: 'defect.detail.error.invalidTransition',
};

/** Design-Luecke bis NEX-1690: Aufbau folgt den Detailseiten der Objektbibliothek und des Testergebnisses. */
@Component({
  selector: 'app-defect-detail',
  imports: [
    TranslatePipe,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    PersonSelectComponent,
  ],
  templateUrl: './defect-detail.component.html',
  styleUrl: './defect-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly defectService = inject(DefectService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly testRunService = inject(TestRunService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private request?: Subscription;

  readonly isProjectReader = inject(ProjectRoleService).isProjectReader;
  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly defectId: string = this.route.snapshot.params['defectId'];

  readonly state = signal<LoadState>('loading');
  readonly defect = signal<Defect | null>(null);
  readonly saving = signal(false);
  readonly errorText = signal<string | null>(null);
  readonly versionConflict = signal(false);
  private readonly members = signal<ProjectMemberDTO[]>([]);

  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.members()));
  readonly statusChoices = computed<DefectStatus[]>(() => {
    const current = this.defect();
    return current ? statusChoices(current.status) : [];
  });
  readonly statusLocked = computed(() => this.isProjectReader() || this.saving() || this.statusChoices().length < 2);

  readonly assigneeControl = new FormControl<string | null>(null);

  ngOnInit(): void {
    this.memberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => this.members.set(members));
    this.assigneeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userId) => this.assign(userId));
    this.load();
  }

  load(): void {
    this.request?.unsubscribe();
    this.state.set('loading');
    this.errorText.set(null);
    this.versionConflict.set(false);
    this.request = this.defectService
      .get(this.projectId, this.defectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (defect) => {
          this.show(defect);
          this.state.set('ok');
        },
        error: (err: unknown) =>
          this.state.set(err instanceof HttpErrorResponse && err.status === 404 ? 'notFound' : 'error'),
      });
  }

  changeStatus(select: HTMLSelectElement): void {
    const current = this.defect();
    if (!current || select.value === current.status) return;
    this.save({ status: select.value as DefectStatus, version: current.version }, () => {
      select.value = current.status;
    });
  }

  download(evidence: DefectEvidence): void {
    this.errorText.set(null);
    this.testRunService
      .downloadStepResultAttachment(this.projectId, evidence.stepResultId, evidence.attachmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => saveBlob(blob, evidence.originalFilename),
        error: () => this.errorText.set(this.translate.instant('defect.detail.error.downloadFailed')),
      });
  }

  private assign(userId: string | null): void {
    const current = this.defect();
    if (!current || userId === (current.assignee?.userId ?? null)) return;
    const req: UpdateDefectRequest = userId
      ? { assigneeUserId: userId, version: current.version }
      : { unassign: true, version: current.version };
    this.save(req, () => this.assigneeControl.setValue(current.assignee?.userId ?? null, { emitEvent: false }));
  }

  private save(req: UpdateDefectRequest, revert: () => void): void {
    this.saving.set(true);
    this.errorText.set(null);
    this.versionConflict.set(false);
    this.defectService
      .update(this.projectId, this.defectId, req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (defect) => {
          this.show(defect);
          this.saving.set(false);
        },
        error: (err: unknown) => {
          revert();
          this.errorText.set(this.updateErrorText(err));
          this.saving.set(false);
        },
      });
  }

  private show(defect: Defect): void {
    this.defect.set(defect);
    this.assigneeControl.setValue(defect.assignee?.userId ?? null, { emitEvent: false });
    if (this.isProjectReader()) {
      this.assigneeControl.disable({ emitEvent: false });
    } else {
      this.assigneeControl.enable({ emitEvent: false });
    }
  }

  private updateErrorText(err: unknown): string {
    const code =
      err instanceof HttpErrorResponse && err.status === 409 ? (err.error as { code?: unknown })?.code : null;
    if (typeof code === 'string' && CONFLICT_KEYS[code]) {
      this.versionConflict.set(code === 'DEFECT_VERSION_CONFLICT');
      return this.translate.instant(CONFLICT_KEYS[code]);
    }
    return serverMessage(err) ?? this.translate.instant('defect.detail.error.updateFailed');
  }
}
